import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import API from "../../services/api";
import { fmtDate, normalizeRows, orDash } from "./AccountsShared";
import "./AccountsPayments.css";

const CREATE_FIELDS = [
  { key: "vendor", label: "Vendor", required: true },
  { key: "amount", label: "Amount", type: "number", required: true },
  { key: "mode", label: "Mode" },
  { key: "referenceNo", label: "Reference No" },
];

export const AccountsPayments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({});
  const [query, setQuery] = useState("");

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/accounts/payments");
      setRows(normalizeRows(res.data));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load payments");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
  }, []);

  const canCreate = ["finance:payments:update"].some((p) => perms.includes(p));
  const canApprove = ["finance:payments:approve", "finance:payments:release"].some((p) => perms.includes(p));
  const canRelease = perms.includes("finance:payments:release");

  const openCreate = () => {
    const init = {};
    CREATE_FIELDS.forEach((f) => (init[f.key] = ""));
    setForm(init);
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post("/employee/accounts/payments", form);
      setShowCreate(false);
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Create failed");
    }
  };

  const updateStatus = async (id, status) => {
    const actionLabel = String(status || "").toLowerCase() === "released"
      ? "Release payment and auto-send WhatsApp notification?"
      : `Update payment status to ${status}?`;
    if (!window.confirm(actionLabel)) return;
    try {
      await API.patch(`/employee/accounts/payments/${id}/status`, { status });
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Update failed");
    }
  };

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const text = [r.vendor?.name, r.vendor, r.referenceNo, r.mode, r.amount]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(q);
    });
  }, [rows, query]);

  const statusTone = (value) => {
    const v = String(value || "").toLowerCase();
    if (v.includes("released") || v.includes("paid")) return "released";
    if (v.includes("pending")) return "pending";
    if (v.includes("failed")) return "failed";
    return "default";
  };

  const initials = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "NA";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const totals = useMemo(() => {
    let released = 0;
    let pending = 0;
    let failed = 0;
    rows.forEach((r) => {
      const amount = Number(r.amount) || 0;
      const status = String(r.status || "").toLowerCase();
      if (status.includes("failed")) failed += amount;
      else if (status.includes("pending")) pending += amount;
      else released += amount;
    });
    return { released, pending, failed };
  }, [rows]);

  return (
    <div className="payments-page">
      <div className="payments-head">
        <div>
          <h2 className="payments-title">Payments</h2>
          <p className="payments-subtitle">Approve, release, and track outbound payments across all accounts.</p>
        </div>
        {canCreate ? (
          <button className="payments-primary" type="button" onClick={openCreate}>
            <span className="material-symbols-outlined">add_circle</span>
            Record Payment
          </button>
        ) : null}
      </div>

      <div className="payments-toolbar">
        <div className="payments-search">
          <span className="material-symbols-outlined">search</span>
          <input
            placeholder="Search by vendor, reference, or amount..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="payments-filters">
          <button type="button">
            <span className="material-symbols-outlined">calendar_today</span>
            Oct 1 - Oct 31, 2023
            <span className="material-symbols-outlined">expand_more</span>
          </button>
          <button type="button">
            <span className="label">Mode:</span>
            All
            <span className="material-symbols-outlined">expand_more</span>
          </button>
          <button type="button">
            <span className="label">Status:</span>
            All
            <span className="material-symbols-outlined">expand_more</span>
          </button>
          <span className="divider" />
          <button type="button" className="more">
            <span className="material-symbols-outlined">filter_list</span>
            More Filters
          </button>
        </div>
      </div>

      {err && <Alert variant="danger" className="mx-4">{err}</Alert>}

      <div className="payments-table-card">
        {loading ? (
          <div className="payments-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="payments-table-wrap">
            <table className="payments-table">
              <thead>
                <tr>
                  <th className="check-col"><input type="checkbox" /></th>
                  <th>Vendor</th>
                  <th>Reference</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="payments-empty">No payments found</td>
                  </tr>
                ) : (
                  filteredRows.map((r, i) => (
                    <tr key={r._id || r.id || i}>
                      <td className="check-col"><input type="checkbox" /></td>
                      <td>
                        <div className="vendor-cell">
                          <div className="avatar">{initials(r.vendor?.name || r.vendor)}</div>
                          <span>{orDash(r.vendor?.name || r.vendor)}</span>
                        </div>
                      </td>
                      <td className="muted">{orDash(r.referenceNo || r.refNo || r.invoiceNo)}</td>
                      <td className="amount">₹ {orDash(r.amount)}</td>
                      <td>
                        <span className="mode-pill">{orDash(r.mode || "ACH")}</span>
                      </td>
                      <td>
                        <span className={`status-pill ${statusTone(r.status)}`}>
                          <span className="dot" />
                          {orDash(r.status || "Pending")}
                        </span>
                      </td>
                      <td className="muted">{fmtDate(r.createdAt)}</td>
                      <td className="actions">
                        {canApprove ? (
                          <>
                            {String(r.status || "").toLowerCase() === "pending" && <><button className="action approve" type="button" onClick={() => updateStatus(r._id, "approved")}>Approve</button><button className="action reject" type="button" onClick={() => updateStatus(r._id, "rejected")}>Reject</button></>}
                            {canRelease && String(r.status || "").toLowerCase() === "approved" && <button className="action approve" type="button" onClick={async () => { if (!window.confirm("Release approved payment?")) return; try { await API.patch(`/employee/accounts/payments/${r._id}/release`, {}); fetchRows(); } catch (e1) { setErr(e1?.response?.data?.message || "Release failed"); } }}>Release</button>}
                          </>
                        ) : (
                          <button className="action more" type="button">
                            <span className="material-symbols-outlined">more_horiz</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="payments-pagination">
          <p>Showing 1 to {Math.min(filteredRows.length, 10)} of {filteredRows.length} payments</p>
          <div className="payments-pages">
            <button type="button" disabled>
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <button type="button" className="active">1</button>
            <button type="button">2</button>
            <button type="button">3</button>
            <span className="ellipsis">...</span>
            <button type="button">10</button>
            <button type="button">
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <div className="payments-stats">
        <div className="stat-card">
          <p>Total Released</p>
          <h3>₹ {totals.released.toLocaleString()}</h3>
          <span className="trend positive">
            <span className="material-symbols-outlined">trending_up</span>
            +12.5% from last month
          </span>
        </div>
        <div className="stat-card">
          <p>Pending Approval</p>
          <h3>₹ {totals.pending.toLocaleString()}</h3>
          <span className="muted">{rows.filter((r) => String(r.status || "").toLowerCase().includes("pending")).length} payments requiring action</span>
        </div>
        <div className="stat-card">
          <p>Failed Attempts</p>
          <h3>₹ {totals.failed.toLocaleString()}</h3>
          <span className="trend negative">
            <span className="material-symbols-outlined">error</span>
            Immediate action required
          </span>
        </div>
      </div>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} fullscreen scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Record Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitCreate}>
            {CREATE_FIELDS.map((f) => (
              <Form.Group className="mb-3" key={f.key}>
                <Form.Label>{f.label}</Form.Label>
                <Form.Control
                  type={f.type || "text"}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  required={!!f.required}
                />
              </Form.Group>
            ))}
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};
